<template>

  <v-row>
    <v-col cols="12">
      <v-table-toolbar>
        <v-spacer/>
        <toolbar-menu :label="$t('common.columns')" icon="far fa-columns">
          <v-list-item>
            <v-checkbox-btn
              :label="$t('accessCode.id')"
              v-model="columns.id.visible"
              :color="columns.id.visible ? 'primary' : ''"
              data-pw="voucherIdColumn"
            />
          </v-list-item>
          <v-list-item>
            <v-checkbox-btn
              :label="$t('accessCode.code')"
              v-model="columns.code.visible"
              :color="columns.code.visible ? 'primary' : ''"
              data-pw="voucherCodeColumn"
            />
          </v-list-item>
          <v-list-item>
            <v-checkbox-btn
              :label="$t('accessCode.user')"
              v-model="columns.user.visible"
              :color="columns.user.visible ? 'primary' : ''"
              data-pw="voucherUserColumn"
            />
          </v-list-item>
          <v-list-item>
            <v-checkbox-btn
              :label="$t('accessCode.account_type')"
              v-model="columns.accountType.visible"
              :color="columns.accountType.visible ? 'primary' : ''"
              data-pw="voucherAccountTypeColumn"
            />
          </v-list-item>
          <v-list-item v-if="isAdminOrRoot">
            <v-checkbox-btn
              :label="$t('accessCode.research')"
              v-model="columns.research.visible"
              :color="columns.research.visible ? 'primary' : ''"
              data-pw="voucherResearchColumn"
            />
          </v-list-item>
          <v-list-item>
            <v-checkbox-btn
              :label="$t('accessCode.used_at')"
              v-model="columns.usedAt.visible"
              :color="columns.usedAt.visible ? 'primary' : ''"
              data-pw="voucherUsedAtColumn"
            />
          </v-list-item>
          <v-list-item>
            <v-checkbox-btn
              :label="$t('common.created_at')"
              v-model="columns.createdAt.visible"
              :color="columns.createdAt.visible ? 'primary' : ''"
              data-pw="voucherCreatedAtColumn"
            />
          </v-list-item>
        </toolbar-menu>

        <data-export-download-menu :formats="['csv', 'excel']" class="mr-4" @click="handleExportClick"/>

        <v-btn-primary
          v-if="isAdminOrRoot"
          prepend-icon="far fa-plus"
          @click="showCreateVoucher = true"
          data-pw="createVoucherButton"
        >
          {{ $t("buttons.create") }}
        </v-btn-primary>
      </v-table-toolbar>

    </v-col>
  </v-row>
  <v-row>
    <v-col md="3" lg="2">
      <table-filter-card @resetFilters="handleReset" :active-filter-count="activeFilterCount">
        <table-filter-card-item title="Search">
          <search-text-field
            :label="$t('components.AccessCodeTablePage.search')"
            data-pw="codesSearchBox"
            :search-string="columns.code.filterText"
            @update:text="(text: string) => {
              handleAccessCodeSearch(text)
            }"
            @change="columns.code.filterText = columns.code.filter as string"
          />
        </table-filter-card-item>
        <table-filter-card-item v-if="!hiddenFields.includes('tenant')"  title="Tenant">
          <tenant-search-autocomplete
            v-if="initialised"
            v-model="selectedTenant"
            entity-type="tenant"
          />
        </table-filter-card-item>
        <table-filter-card-item v-if="!hiddenFields.includes('institution')" title="Institution">
          <tenant-search-autocomplete
            v-if="initialised"
            v-model="selectedInstitution"
            entity-type="institution"
            :tenant-ids="selectedTenant ? [selectedTenant] : []"
          />
        </table-filter-card-item>
        <table-filter-card-item v-if="!hiddenFields.includes('cohort')" title="Cohort">
          <tenant-search-autocomplete
            v-if="initialised"
            v-model="selectedCohort"
            entity-type="cohort"
            :tenant-ids="selectedTenant ? [selectedTenant] : []"
            :institution-ids="selectedInstitution ? [selectedInstitution] : []"
          />
        </table-filter-card-item>
        <table-filter-card-item title="Account Type">
          <select-account-type
            :label="columns.accountType.title"
            v-model="columns.accountType.filter"
            @update:modelValue="columns.accountType.filterText = t(`accountTypes.${columns.accountType.filter}`)"
          />
        </table-filter-card-item>
        <table-filter-card-item title="Used">
          <v-radio-group
            :disabled="store.networkBusy"
            v-model="columns.used.filter"
            data-pw="usedAtFilter"
            density="compact"
          >
            <v-radio
              :label="$t('common.yes')"
              value="true"
              data-pw="usedAtFilterYes"
              @change="columns.used.filterText = 'Yes'"
            />
            <v-radio
              :label="$t('common.no')"
              value="false"
              data-pw="usedAtFilterNo"
              @change="columns.used.filterText = 'No'"
            />
            <v-radio
              :label="$t('common.all')"
              value="all"
              data-pw="usedAtFilterAll"
              @change="columns.used.filterText = 'All'"
            />
          </v-radio-group>
        </table-filter-card-item>
        <table-filter-card-item v-if="isAdminOrRoot" title="Research">
          <v-radio-group
            :disabled="store.networkBusy"
            v-model="columns.research.filter"
            data-pw="researchFilter"
            density="compact"
          >
            <v-radio
              :label="$t('common.yes')"
              value="true"
              data-pw="researchFilterYes"
              @change="columns.research.filterText = 'Yes'"
            />
            <v-radio
              :label="$t('common.no')"
              value="false"
              data-pw="researchFilterNo"
              @change="columns.research.filterText = 'No'"
            />
            <v-radio
              :label="$t('common.all')"
              value="all"
              data-pw="researchFilterAll"
              @change="columns.research.filterText = 'All'"
            />
          </v-radio-group>
        </table-filter-card-item>
        <table-filter-card-item title="Created At" :open-by-default="false">
          <v-menu
            v-model="datePickerMenu"
            :close-on-content-click="false"
            location="end"
            max-width="600"
          >
            <template v-slot:activator="{ props }">
              <v-text-field
                v-bind="props"
                :model-value="formatDateRange(createdAtDateFilter)"
                label="Select date range"
                readonly
                variant="outlined"
                density="compact"
                prepend-inner-icon="mdi-calendar"
                clearable
                @click:clear="createdAtDateFilter = null"
              />
            </template>
            <v-card>
              <vue-date-picker
                v-model="createdAtDateFilter"
                range
                auto-apply
                multi-calendars
                :enable-time-picker="false"
                inline
                @update:model-value="datePickerMenu = false"
              />
            </v-card>
          </v-menu>
        </table-filter-card-item>
      </table-filter-card>
    </v-col>
    <v-col md="9" lg="10">
      <v-data-table-server
        v-model:="selectedItems"
        class="mb-6"
        :loading="loadingData"
        :headers="headers"
        :items="accessCodes"
        :items-per-page="perPage"
        hide-default-footer
        data-pw="accessCodeTable"
        @update:sort-by="onSort"
        :items-length="0"
        @click:row="onClickRow"
        :sort-by="sort"
        :hover="true"
        show-select
        return-object
      >
        <template v-slot:item.id="{ item }">
          <v-checkbox-btn
            v-model="selectedItems"
            :value="item._id"
          ></v-checkbox-btn>
        </template>

        <template v-slot:no-data>
          <h5 class="pa-2 d-flex align-center justify-center align-items-center">
            {{ $t("components.AccessCodeTablePage.no_data_message") }}
          </h5>
        </template>

        <template v-slot:loader>
          <v-sheet
            class="d-flex align-center justify-center"
            height="100%"
          >
            <v-progress-linear
              indeterminate
              color="primary"
            ></v-progress-linear>
          </v-sheet>
        </template>

        <template v-slot:top>
          <div class="">
            <div v-if="activeFilterCount > 0" class="px-2 pb-2">
              <b>{{ $t("common.filters") }}</b>
              <template v-for="(column) in columns">
                <v-chip
                  :key="`${column.modelKey}_filter`"
                  class="ml-2"
                  size="small"
                  v-if="column.filter && column.filterText">{{ column.title }}: <b>
                    {{ column.filterText }}</b></v-chip>
              </template>
            </div>
          </div>

          <!-- Bulk Actions Toolbar -->
          <v-toolbar
            v-if="selectedItems.length > 0"
            class="bulk-actions-toolbar"
            color="primary"
            dark
            dense
            flat
          >
            <v-toolbar-title class="text-body-2">
              {{ selectedItems.length }} {{ selectedItems.length === 1 ? 'access code' : 'access codes' }} selected
            </v-toolbar-title>

            <v-spacer />

            <v-btn
              variant="text"
              size="small"
              @click="selectedItems = []"
            >
              Clear Selection
            </v-btn>

            <v-divider vertical class="mx-2" />

            <v-btn
              v-if="isAdminOrRoot"
              variant="text"
              size="small"
              :disabled="!enableDelete"
              @click="showDeleteVouchers = true"
              data-pw="deleteVoucherButton"
            >
              {{ $t("buttons.delete") }}
            </v-btn>

          </v-toolbar>
        </template>

        <template v-slot:item.code="{ item }">
          <div class="bg-blue-grey-darken-1 rounded pa-1 font-weight-bold text-center">
            {{ item.code }}
          </div>
        </template>

        <template v-slot:item.accountType="{ item }">
          <v-account-type-chip :account-type="item.accountType" />
        </template>

        <template v-slot:item.user="{ item }">
          <div v-if="item.user">
            <div class="font-weight-bold">
              <span v-if="(item.user as DashUser).profile.firstName">{{ (item["user"] as DashUser).profile.firstName }} &nbsp;</span>
              <span v-if="(item.user as DashUser).profile.lastName">{{
                (item["user"] as DashUser).profile.lastName
              }}</span>
            </div>
            <v-chip size="x-small" v-if="(item.user as DashUser).email" class="mr-2">{{
              (item["user"] as DashUser).email
            }}
            </v-chip>
            <v-chip size="x-small" v-if="(item.user as DashUser).phone">{{ (item["user"] as DashUser).phone }}</v-chip>

          </div>
          <div v-else>-</div>
        </template>

        <template v-slot:item.research="{ item }">
          <v-icon
            v-if="item['research']"
            size="x-small"
          >
            far fa-check
          </v-icon>
        </template>

        <template v-slot:item.usedAt="{ item }">
          <div class="d-flex align-center">
            <v-icon color="red" v-if="!item.usedAt" class="mr-1">far fa-xmark fa-regular</v-icon>
            <template v-else>
              <v-icon color="green" class="mr-1">far fa-check</v-icon>
              {{ toLocaleDateTime(item["usedAt"]) }}
            </template>
          </div>
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ toLocaleDateTime(item["createdAt"]) }}
        </template>

        <template v-slot:bottom>
          <v-divider/>
          <pagination-controls
            v-if="paginatedData"
            class="text-center pt-2 v-table__pagination-controls"
            :pagination-footer-data="paginationFooterData"
            @update:per-page="onUpdatePerPage"
            @on-next="onNext"
            @on-prev="onPrev"
            @on-page-change="onPageChange"
            data-pw="accessCodePaginationControls"
          />
        </template>
      </v-data-table-server>

    </v-col>
  </v-row>

  <v-dialog
    v-model="showExportDialog"
    max-width="500"
  >
    <export-voucher
      @close="showExportDialog = false"
      @download="exportPages"
      :format="exportFormat"
      :cols="cols"
    >
    </export-voucher>
  </v-dialog>

  <v-dialog v-model="showCreateVoucher" :persistent="true" max-width="500">
    <create-voucher-card
      @close="onCloseCreateDialog"
      :user-choice=true
    />
  </v-dialog>

  <v-dialog v-model="showDeleteVouchers" persistent max-width="500">
    <delete-vouchers
      @close="onCloseDeleteVoucherDialog"
      :multiple-voucher-ids="selectedItems"
      data-pw="deleteVouchersDialog"
    />
  </v-dialog>

  <v-snackbar v-model="showExportingSnackBar">
    {{ $t("components.AccessCodeTablePage.export_message") }}
    <v-progress-linear :indeterminate="true"/>
  </v-snackbar>
</template>

<script setup lang="ts">
import {onMounted, ref, Ref, watch, PropType} from "vue";
import yapi from "@/lib/yapi";
import {useI18n} from "vue-i18n";
import {useRouter, useRoute} from "vue-router";
import {toLocaleDateTime} from "@/lib/time";
import {
  AccessCode,
  AccessCodeExportSelectors,
  AccessCodePaginationParams,
  AccountType,
  isYapiError,
  Paginated,
  Role,
  Tenant,
  Institution,
  Cohort,
  YapiError, DashUser
} from "@YenzaCT/sdk";

import ExportVoucher from "@/pages/Licenses/AccessCodeTablePage/components/ExportAccessCodes.vue";
import PaginationControls from "@/components/PaginationControls.vue";
import SearchTextField from "@/components/SearchTextField.vue";
import {useGlobalStore} from "@/store";
import CreateVoucherCard from "@/components/Voucher/CreateVoucherCard.vue";
import {ColumnsConfig, FooterData, footerDataFactory, usePagination} from "@/lib/pagination";
import ToolbarMenu from "@/components/DataTable/ToolbarMenu.vue";
import SelectAccountType from "@/components/SelectAccountType.vue";
import {downloadExcel, downloadCSV} from "@/lib/fileDownload";
import {DateTime} from "luxon";
import VueDatePicker from "@vuepic/vue-datepicker";
import DataExportDownloadMenu from "@/components/DataExportDownloadMenu.vue";
import DeleteVouchers from "@/components/Voucher/DeleteVouchers.vue";
import VAccountTypeChip from "@/components/VAccountTypeChip.vue";
import TableFilterCardItem from "@/components/TableFilterCardItem.vue";
import TableFilterCard from "@/components/TableFilterCard.vue";
import TenantSearchAutocomplete from "@/components/FormFields/TenantSearchAutocomplete.vue";

const store = useGlobalStore();
const router = useRouter();
const route = useRoute();

const {t} = useI18n();

const isAdminOrRoot = ref(store.user?.app.role === Role.Admin || store.user?.app.role === Role.Root);

const showExportDialog = ref(false);
const initialised = ref(false);
const loadingData = ref(false);
const exportFormat = ref("csv");
const showCreateVoucher = ref(false);
const accessCodes: Ref<AccessCode[]> = ref([]);
const showDeleteVouchers = ref(false);
const paginatedData: Ref<Paginated<AccessCode> | undefined> = ref();
let params: AccessCodePaginationParams;
let paginationFooterData: FooterData = footerDataFactory();

const props = defineProps({
  tenant: {
    type: Object as PropType<Tenant>,
    required: false
  },
  institution: {
    type: Object as PropType<Institution>,
    required: false
  },
  cohort: {
    type: Object as PropType<Cohort>,
    required: false
  },
  hiddenFields: {
    type: Array as PropType<string[]>,
    default: (): string[] => []
  },
  clickRoute: {
    type: String as PropType<string>,
    default: "accessCode"
  },
  routeParams: {
    type: Object as PropType<Record<string, unknown>>,
    default: () => ({})
  }
});

const showExportingSnackBar = ref<boolean>(false);

const datePickerMenu = ref(false);

const accessCodeSearchString = ref(route.query.search as string);
const selectedItems = ref<string[]>([]);

const cols: ColumnsConfig = {
  id: {
    title: t("accessCode.id"),
    modelKey: "_id",
    visible: false,
    filter: "",
    filterText: ""
  },
  code: {
    title: t("accessCode.code"),
    modelKey: "code",
    visible: true,
    filter: "",
    filterText: ""
  },
  tenant: {
    title: "Tenant",
    modelKey: "tenantMeta.title",
    visible: !props.hiddenFields.includes("tenantMeta"),
    filter: "",
    filterText: "",
  },
  institution: {
    title: "Institution",
    modelKey: "institutionMeta.title",
    visible: !props.hiddenFields.includes("institutionMeta"),
    filter: "",
    filterText: ""
  },
  cohort: {
    title: "Cohort",
    modelKey: "cohortMeta.title",
    visible: !props.hiddenFields.includes("cohortMeta"),
    filter: ""
  },
  user: {
    title: t("accessCode.user"),
    modelKey: "user",
    visible: true,
    filter: "",
    filterText: ""
  },
  accountType: {
    title: t("accessCode.account_type"),
    modelKey: "accountType",
    visible: true,
    filter: "",
    filterText: ""
  },
  research: {
    title: t("accessCode.research"),
    modelKey: "research",
    visible: false,
    filter: "",
    filterText: ""
  },
  createdAt: {
    title: t("common.created_at"),
    modelKey: "createdAt",
    visible: true,
    filter: "",
    filterText: ""
  },
  used: {
    title: t("accessCode.used"),
    modelKey: "usedAt",
    visible: false,
    filter: "",
    filterText: ""
  },
  usedAt: {
    title: t("accessCode.used_at"),
    modelKey: "usedAt",
    visible: true,
    filter: "",
    filterText: ""
  }
};

const {
  headers,
  page,
  perPage,
  sort,
  activeFilterCount,
  createdAtDateFilter,
  selectedTenant,
  selectedInstitution,
  selectedCohort,
  watchDateFilter,
  resetFilter,
  createUrlFilterParameters,
  onUpdatePerPage,
  onSort,
  onNext,
  onPrev,
  columns,
  onPageChange,
  setupFooterData,
} = usePagination(fetchPage, {
  initialColumns: cols,
  initialSort: [{
    key: "createdAt",
    order: "desc"
  }]
});

onMounted(async () => {
  loadingData.value = true;

  if (accessCodeSearchString.value) {
    handleAccessCodeSearch(accessCodeSearchString.value);
    return;
  }

  if (props.cohort)
    setupCohortFilter();

  if (props.institution && props.institution._id)
    setupInstitutionFilter();

  if (props.tenant && props.tenant._id)
    setupTenantFilter();

  setupParams();

  initialised.value = true;

  await fetchPage();

  loadingData.value = false;
});

function setupTenantFilter() {
  if (props.tenant && props.tenant._id) {
    columns.tenant.filter = props.tenant._id;
    selectedTenant.value = props.tenant._id;
  } else {
    columns.tenant.filter = route.query.filterTenant as string || columns.tenant.filter;
    selectedTenant.value = route.query.filterTenant as string || null;
  }
}

function setupInstitutionFilter() {
  if (props.institution && props.institution._id) {
    columns.institution.filter = props.institution._id;
    selectedInstitution.value = props.institution._id;
  } else {
    columns.institution.filter = route.query.filterInstitution as string || columns.institution.filter;
    selectedInstitution.value = route.query.filterInstitution as string || null;
  }
}

function setupCohortFilter() {
  if (props.cohort) {
    columns.cohort.filter = props.cohort._id as string;
    columns.cohort.filterText = props.cohort.title;
    selectedCohort.value = props.cohort._id as string;
  } else {
    columns.cohort.filter = route.query.filterCohort as string || columns.cohort.filter;
    selectedCohort.value = route.query.filterCohort as string || null;
  }
}

/**
 * Watch for changes to the search query param when using global search
 */
watch(() => route.query.search, async (newSearchValue) => {
  if (newSearchValue && newSearchValue !== accessCodeSearchString.value) {
    resetFilter(props.hiddenFields);
    handleAccessCodeSearch(newSearchValue as string);
    setupParams();
    await fetchPage();
  }
});

watchDateFilter(createdAtDateFilter, columns.createdAt);

const showDelete = ref(false);
const enableDelete = ref(false);

watch(selectedItems, (newSelectedItems) => {
  showDelete.value = newSelectedItems.length > 0;
  enableDelete.value = selectedItems.value.every((item) =>
    !Object.prototype.hasOwnProperty.call(item, "usedAt"));
});

function handleExportClick(format: string) {
  showExportDialog.value = true;
  exportFormat.value = format;
}

function handleReset() {
  resetFilter(props.hiddenFields);
}

function setupParams() {
  page.value = route.query.page ? parseInt(route.query.page as string) : page.value;
  perPage.value = route.query.size ? parseInt(route.query.size as string) : perPage.value;
  sort.value[0].key = route.query.sortBy as string || sort.value[0].key;
  sort.value[0].order = route.query.sortOrder as "asc" | "desc" || sort.value[0].order;

  columns.code.filter = route.query.filterCode as string || columns.code.filter;
  columns.accountType.filter = route.query.filterAccountType as AccountType || columns.accountType.filter;

  columns.tenant.filter = route.query.filterTenant as string || columns.tenant.filter;
  columns.institution.filter = route.query.filterInstitution as string || columns.institution.filter;
  columns.cohort.filter = route.query.filterCohorts as string || columns.cohort.filter;
}

function setupDateRangeFromFilterParams(initialParams: AccessCodePaginationParams): AccessCodePaginationParams {
  const params: AccessCodePaginationParams = {...initialParams};

  params.filterCreatedAtStart = DateTime.fromISO(columns.createdAt.filter[0]).startOf("day").toISO() as string || undefined;
  params.filterCreatedAtEnd = DateTime.fromISO(columns.createdAt.filter[1]).endOf("day").toISO() as string || undefined;

  return params;
}

async function exportPages(selected: AccessCodeExportSelectors) {
  // Close the dialog
  showExportDialog.value = false;

  const params = setupDateRangeFromFilterParams({});

  if (cols.cohort.filter) {
    params.filterCohorts = cols.cohort.filter as string;
  }

  if (cols.tenant.filter) {
    params.filterTenant = cols.tenant.filter as string;
  }

  if (cols.institution.filter) {
    params.filterInstitution = cols.institution.filter as string;
  }

  if (cols.code.filter) {
    params.filterCode = cols.code.filter as string;
  }

  if (cols.accountType.filter) {
    params.filterAccountType = cols.accountType.filter as AccountType;
  }

  if (cols.used.filter === "true") {
    params.filterUsed = true;
  } else if (cols.used.filter === "false") {
    params.filterUsed = false;
  }

  if (cols.research.filter === "true") {
    params.filterResearch = true;
  } else if (cols.research.filter === "false") {
    params.filterResearch = false;
  }

  if (exportFormat.value === "excel")
    params.format = "excel";
  else params.format = "csv";

  try {
    store.networkBusy = true;
    showExportingSnackBar.value = true;
    const res = await yapi.admin.accessCode.export({selectors: selected}, params);

    const timestamp = DateTime.now().toFormat("yyyy-MM-dd HH-mm-ss");

    if (exportFormat.value === "excel") {
      downloadExcel(res.data.content.toString(), `vouchers ${timestamp}`);
    } else {
      downloadCSV(res.data.content.toString(), `vouchers ${timestamp}`);
    }
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    store.networkBusy = false;
    showExportingSnackBar.value = false;
  }
}

async function fetchPage() {
  loadingData.value = true;
  params = {
    page: page.value,
    size: perPage.value,
    sortBy: sort.value[0].key,
    sortOrder: sort.value[0].order,

    filterCode: columns.code.filter as string || undefined,
    filterAccountType: columns.accountType.filter as AccountType || undefined,
    filterTenant: columns.tenant.filter as string || undefined,
    filterInstitution: columns.institution.filter as string || undefined,
    filterCohorts: columns.cohort.filter as string || undefined,
    filterCreatedAtStart: DateTime.fromISO(columns.createdAt.filter[0]).startOf("day").toISO() as string || undefined,
    filterCreatedAtEnd: DateTime.fromISO(columns.createdAt.filter[1]).endOf("day").toISO() as string || undefined,
  };

  if (cols.used.filter === "true") {
    params.filterUsed = true;
  } else if (cols.used.filter === "false") {
    params.filterUsed = false;
  }

  if (cols.research.filter === "true") {
    params.filterResearch = true;
  } else if (cols.research.filter === "false") {
    params.filterResearch = false;
  }

  try {
    store.networkBusy = true;
    paginatedData.value = (await yapi.admin.accessCode.paginate(params)).data;
    await router.push({query: createUrlFilterParameters(params)});
    const footerData = Object.assign(paginatedData.value);
    footerData.totalPages = paginatedData.value?.hasNextPage
      ? paginatedData.value.page + 1
      : paginatedData.value.page;
    paginationFooterData = setupFooterData(footerData);
    if (paginatedData.value)
      accessCodes.value = paginatedData.value.docs;
    selectedItems.value = [];
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    store.networkBusy = false;
    loadingData.value = false;
  }
}

async function onCloseCreateDialog() {
  showCreateVoucher.value = false;
  await fetchPage();
}

const onClickRow = async (event: Event, row: { internalItem: { raw?: AccessCode } }) => {
  try {
    if (row.internalItem && row.internalItem.raw) {
      const accessCodeId = row.internalItem.raw._id;
      if (accessCodeId) {
        await openAccessCode(row.internalItem.raw);
      }
    }
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  }
};

function handleAccessCodeSearch(searchText: string) {
  columns.code.filter = searchText;
  columns.code.filterText = searchText;
}

function formatDateRange(dateRange: undefined | Date[]): string {
  if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) {
    return "";
  }

  const formatDate = (date: Date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString();
  };

  const [start, end] = dateRange;
  if (start && end) {
    return `${formatDate(start)} - ${formatDate(end)}`;
  } else if (start) {
    return formatDate(start);
  }
  return "";
}

const openAccessCode = async (accessCode: AccessCode) => {
  const routeName = props.clickRoute || "accessCode";

  let params: Record<string, unknown>;
  if (routeName === "tenantAccessCode") {
    params = {tenantId: accessCode.tenantMeta?._id, id: accessCode?._id};
  } else if (routeName === "institutionAccessCode") {
    params = {tenantId: accessCode.tenantMeta?._id, institutionId: accessCode.institutionMeta?._id, id: accessCode._id};
  } else if (routeName === "cohortAccessCode") {
    params = {tenantId: accessCode.tenantMeta?._id,
      institutionId: accessCode.institutionMeta?._id,
      cohortId: accessCode.cohortMeta?._id,
      id: accessCode._id};
  } else {
    params = {...props.routeParams, id: accessCode._id};
  }

  await router.push({
    name: routeName,
    params: {
      ...params,
      id: accessCode._id
    }
  });
};

async function onCloseDeleteVoucherDialog() {
  await fetchPage();
  showDeleteVouchers.value = false;
  selectedItems.value = [];
}
</script>

<style>
.bulk-actions-toolbar {
  margin-top: -1px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
